import { useMemo } from 'react';
import {
  ArtifactsService,
  type BatchArtifactDetailItemResponseDto,
} from '@inference.engine/api-client/web';
import { useQuery } from '@tanstack/react-query';
import { inferenceHttp } from '@/lib/api/http-client';
import { chunk } from '@/shared/utils/chunk';
import type { WorkspaceArtifactRow } from '../ui/ExistingArtifactsPicker/ExistingArtifactsPicker.types';

/**
 * Stable per-version cache id for a picker row: the `(contextId, key, version)`
 * tuple the api batch-detail endpoint resolves against. Versions are immutable,
 * so this key uniquely identifies one render-data payload forever. A null
 * contextId (workspace-owned artifact) stringifies to `null`, matching the same
 * key built from the batch-detail response row.
 */
export function getPickerArtifactDetailKey(tuple: {
  contextId: string | null;
  key: string;
  version: number;
}): string {
  return `${tuple.contextId}:${tuple.key}:${tuple.version}`;
}

/** api `batchDetail` rejects bodies over 100 tuples — chunk the request to match. */
const BATCH_DETAIL_MAX_ITEMS = 100;

interface PickerArtifactTuple {
  contextId: string | null;
  key: string;
  version: number;
}

/**
 * Derives the resolvable `(contextId, key, version)` tuples for the picker rows
 * whose widget kind has a thumbnail renderer. Rows without a numeric version
 * (uploads surface `null`) can't be addressed by the version-keyed endpoint and
 * are dropped — those kinds fall back to their static thumbnail / palette icon.
 *
 * Exported so the picker can memoize the tuple list (a stable identity keeps
 * the query key — and therefore the request — stable across re-renders).
 */
export function toPickerArtifactTuples(
  rows: WorkspaceArtifactRow[],
  hasThumbnailRenderer: (key: string) => boolean,
): PickerArtifactTuple[] {
  const tuples: PickerArtifactTuple[] = [];
  for (const row of rows) {
    if (row.version == null) continue;
    if (!hasThumbnailRenderer(row.key)) continue;
    tuples.push({ contextId: row.contextId, key: row.key, version: row.version });
  }
  return tuples;
}

/**
 * Bulk-resolves widget render-data for the picker's currently-loaded rows in one
 * round-trip per 100 tuples (`POST :workspaceId/artifacts/batch-detail`), so the
 * picker can mount each widget in thumbnail mode without an N-deep per-row
 * fan-out.
 *
 * The picker is project-independent: it reads its list straight from the api
 * workspace-artifacts endpoint, so it calls the api batch-detail endpoint
 * DIRECTLY (capability model — same per-version semantics and user bearer as the
 * single-version route the user already used to list these rows). It does NOT go
 * through workspace-api's project-scoped detail.
 *
 * Returns a `Map<tupleKey, data>` keyed by `getPickerArtifactDetailKey`; rows
 * read their own payload from it. Gated on a non-empty tuple list so the query
 * stays dormant when no widget rows are visible (or the flag is off and callers
 * pass an empty list).
 */
export function usePickerArtifactDetails(
  workspaceId: string,
  tuples: PickerArtifactTuple[],
): Map<string, Record<string, unknown>> {
  const { data } = useQuery({
    queryKey: ['workspace', workspaceId, 'artifacts', 'picker-detail', tuples],
    queryFn: async (): Promise<BatchArtifactDetailItemResponseDto[]> => {
      const responses = await Promise.all(
        chunk(tuples, BATCH_DETAIL_MAX_ITEMS).map(async (items) => {
          const response = await ArtifactsService.batchDetail({
            client: inferenceHttp,
            path: { workspaceId },
            body: { items },
          });
          if (!response.data) {
            throw new Error('Failed to load picker artifact details');
          }
          return response.data.details;
        }),
      );
      return responses.flat();
    },
    enabled: tuples.length > 0,
    // Context-bound artifact versions are immutable, so their resolved data
    // never changes for a given tuple — never refetch. Null-context
    // (workspace-owned) artifacts are the exception: they're mutable in place at
    // version 1 (`findOrCreateContextlessArtifact` refreshes name/thumbnail/data),
    // so a re-saved one can show a stale preview here until gcTime eviction. That
    // staleness is accepted — bounded by gcTime and matching the grid's
    // bulk-detail policy + SavedWidgetArtifactPreview on main.
    staleTime: Infinity,
  });

  // Stable identity while `data` is unchanged so the consumer's row memos and
  // virtualizer measurements don't churn on unrelated re-renders.
  return useMemo(() => {
    const map = new Map<string, Record<string, unknown>>();
    for (const detail of data ?? []) {
      map.set(getPickerArtifactDetailKey(detail), detail.data);
    }
    return map;
  }, [data]);
}
